<template>
  <div
    :id="vnodeId"
    ref="Container"
    data-identity="CContainer"
    :data-vnode-id="vnodeId"
    :class="classes"
    class="container-element w-100 p-2"
    @click="onEditAction"
    @mouseenter="mouseOverAction"
    @mouseleave="mouseLeaveAction"
    @contextmenu.prevent.stop="showContextMenu"
    @dblclick="resize"
  >
    <div :class="ContainerBgImage" />
    <div :class="ContainerBgColor" />

    <div
      v-if="!fbNode.children && !backgroundImgExist"
      class="d-flex w-100 h-100"
    >
      <img
        src="/img/box-container-placeholder-.svg"
        class="img-fluid"
        style="height: 100%; width: 100%"
        alt="empty container"
      />
    </div>

    <div v-if="fbNode.children" ref="containerStacks" :class="containerInnerClasses">
      <template v-for="node in fbNode.children || []">
        <c-container-item
          v-bind="node.data.props"
          :key="node.data.props.vnodeId"
          :fb-node="node"
          class="c-container-item"
          @removeElement="removeNode"
          @duplicateElement="duplicateNode"
          @forceRemoveElement="forceRemoveNode"
        ></c-container-item>
      </template>
    </div>
  </div>
</template>

<script>
import ContainerAbstractScript from '@/js/components/v3/editor/nodes/abstracts/ContainerAbstractScript'
import ContainerAbstractTemplate from '@/js/components/v3/editor/nodes/abstracts/ContainerAbstractTemplate.vue'
import FbVNode from '@/js/components/base/FbVNode.vue'
import FirebaseNodeV3 from '@/js/components/v3/base/FirebaseNode'
import { AddCommandCContainer } from '@/js/commands/AddCommand'
import * as _ from 'lodash'
import { ManualPropertyChangeCommand } from '@/js/commands/PropertyChangeCommand'
import { CContainerItem } from '@/js/lib/custom-component-descriptors/ccontainer'
import { RemoveContainerCommand } from '@/js/commands/RemoveCommand'
import { CreateWidgetFromFirebaseNode } from '@/js/components/v3/lib/vue-dom-obj'
import { mapGetters, mapMutations } from 'vuex'
import * as getters from '@/js/store/modules/getters'
import * as mutations from '@/js/store/modules/mutations'
import uuid from "uuid/v4";

export default FbVNode.extend({
  name: 'CContainer',
  mixins: [ContainerAbstractScript, FirebaseNodeV3],
  components: {
    ContainerAbstractTemplate,
    'c-container-item': () => import('../c-container/CContainerItem.vue'),
  },
  props: {
    desktop: {
      type: Object,
      default: () => ({}),
    },
    phone: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      name: 'CContainer',
    }
  },
  computed: {
    ...mapGetters({
      viewMode: getters.VIEW_MODE,
    }),
    backgroundImgExist() {
      return (
        this.fbNode?.data?.props?.background?.image != null &&
        this.fbNode?.data?.props?.background?.image !== ''
      )
    },
    containerInnerClasses() {
      return {
        ...this.contentClasses,
        [this.stylesheet.classes.containerInnerLayout]: true,
      }
    },
    otherStyles() {
      return {
        containerInnerLayout: {
          display: 'flex',
          flexDirection: this.flexDirection,
          flexWrap: this.flexWrap,
          justifyContent: this.justifyContent,
          alignItems: this.alignItems,
          alignContent: this.alignContent,
          alignSelf: 'auto',
          flexBasis: 'auto',
          flexGrow: 1,
          flexShrink: 1,
          gap: `${this.verticalGap}px ${this.horizontalGap}px`,
          height: '100%',
          width: '100%',
          paddingInlineEnd: '0',
          paddingInlineStart: '0',
        },
      }
    },
    flexDirection() {
      return this[this.viewMode].flexDirection || 'row'
    },
    flexWrap() {
      return this[this.viewMode].flexWrap || 'wrap'
    },
    justifyContent() {
      return this[this.viewMode].justifyContent || 'start'
    },
    alignItems() {
      return this[this.viewMode].alignItems || 'start'
    },
    alignContent() {
      return this[this.viewMode].alignContent || 'start'
    },
    horizontalGap() {
      return this[this.viewMode].horizontalGap || 0
    },
    verticalGap() {
      return this[this.viewMode].verticalGap || 10
    },
  },
  methods: {
    ...mapMutations({
      ignoreHistory: mutations.HISTORY_IGNORE,
      enableHistory: mutations.HISTORY_LISTEN,
    }),
    async onEditAction(e) {
      await this.deselect()
      this.$nextTick(() => {
        this.edit(e)
      })
    },
    mouseOverAction(e) {
      if (this.index === -1) {
        return
      }
      e.stopPropagation()
      this.$el.classList.add('show-hover-effect')
    },
    mouseLeaveAction(e) {
      if (this.index === -1) {
        return
      }
      e.stopPropagation()
      // this.$parent.$parent.$el.classList.remove('hide-grid-lines')
      this.$el.classList.remove('show-hover-effect')
    },
    async executeCommand(command) {
      await this.validateAndPushCommand(command)
    },
    addItem(elementComponent) {
      const containerItem = new CContainerItem({ ...elementComponent.options })
      containerItem.children.push(new elementComponent.elementType())

      this.executeCommand(new AddCommandCContainer(this.vnodeId, containerItem))
    },
    deepOmit(obj, keysToOmit) {
      if (!obj || typeof obj !== 'object') return obj

      if (Array.isArray(obj)) {
        return obj.map((item) => this.deepOmit(item, keysToOmit))
      }

      // First ensure we omit top-level keys
      const filteredObj = _.omit(obj, keysToOmit)

      // Then recursively process remaining properties
      const result = {}
      Object.keys(filteredObj).forEach((key) => {
        if (key === 'vnodeId') {
          result[key] = uuid()
        } else {
          result[key] = this.deepOmit(filteredObj[key], keysToOmit)
        }
      })

      return result
    },
    async updateChildrenElements(children) {
      const keysToOmit = ['.key', '.path']
      const command = new ManualPropertyChangeCommand(
        this.path(),
        'children',
        children.map((x) => this.deepOmit(x, keysToOmit)),
        this.fbNode.children.map((x) => this.deepOmit(x, keysToOmit)),
        this.vnodeId,
        false,
      )
      await this.executeCommand(command)
    },
    async forceRemoveNode(component) {
      this.fbNode.children = this.fbNode.children.filter(
        (x) => x.data.props.vnodeId !== component.vnodeId,
      )
      // save to database
      await this.firebaseRef.removeNode(+component.idx())
    },
    removeNode(component, execute = true) {
      component.ignore()
      component.deselect()
      const command = new RemoveContainerCommand(
        this.vnodeId,
        component.vnodeId,
        component.fbNode,
      )
      this.executeCommand(command)
    },
    async duplicateNode(node) {
      const keysToOmit = ['.key', '.path']
      this.ignore()
      this.deselect()
      let clonedNode = CreateWidgetFromFirebaseNode(
        {
          data: Object.assign({}, this.deepOmit(node.data, keysToOmit)),
          tag: node.tag,
          children: node.children.map((x) => this.deepOmit(x, keysToOmit)),
        },
        true,
      )
      await this.executeCommand(new AddCommandCContainer(this.vnodeId, clonedNode))
    },
    findParentToBuilder(startFrom) {
      let parent = startFrom.parent()
      if (parent.name === 'GridItemWrapper') {
        return startFrom
      }else if (parent) {
        return this.findParentToBuilder(startFrom.parent())
      }else {
        return null
      }
    },
    resizeContainerHeight() {
      // Fits subgrid container height to content height
      this.$nextTick(() => {
        const gridItemWrapper = this.parent()
        const rootGridBuilder = this.parent().parent()

        const ofHeight =
          this.$refs.containerStacks.offsetHeight +
          rootGridBuilder.getGrid().opts.cellHeight
        const height = Math.round(
          (ofHeight + (rootGridBuilder.getGrid().opts.verticalMargin || 0)) /
          (rootGridBuilder.getGrid().getCellHeight() +
            (rootGridBuilder.getGrid().opts.verticalMargin || 0) || 1),
        )
        if (!rootGridBuilder.itemResizing) {
          // create a command and execute it outside command manager stack
          // because we don't want to track it
          const command = this.createCommand(
            this.viewMode,
            Object.assign(gridItemWrapper.$props[this.viewMode], {
              dataX: gridItemWrapper.dataX,
              dataY: gridItemWrapper.dataY,
              dataWidth: gridItemWrapper.dataWidth,
              dataHeight: height,
            }),
            gridItemWrapper.getVNodeID(),
          )
          command.execute()
        }
      })
    },
    resizeRootContainerHeight() {
      const parentToRoot = this.findParentToBuilder(this)
      if (parentToRoot) {
        parentToRoot.resizeContainerHeight()
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.show-hover-effect {
  cursor: pointer;
  outline: #7c9dd5 solid 3px;
}
</style>
